import React, { useState, useEffect, Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import c from 'classnames';
import { Helmet } from 'react-helmet';

import './index.scss';
import { Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { cloneDeep, isEmpty } from 'lodash';
import { getBenchmarkName, getFiltersAppliedText, getOutputFilter, updateFilterParams } from '../../../utils/functions';
import FocusArea from '../assets/FocusArea.svg';
import viewIcon from '../../Reports/assets/viewIcon.svg';
import { getStaticText } from '../../../utils/constants';
// import FilterBar from '../../../components/FilterBar';
import AuthStore from '../../../common/AuthStore';
import colors from '../../../sass/colors';

const isResurveyRankingSurveyVersion = v => v === '4' || v === '3_2' || v === '4.0';

function ResurveyBoard({
    practiceDataCompYr,
    getComparisonYrPracticeData,
    compOhid,
    apiParams,
    apiLoadingCount,
    practiceData,
    defaultSettings,
    filters,
    setFilters,
    getResurveyParams,
    getPracticeResurveyData,
    resurveyDataHere,
    practiceResurveyData,
    isToggled,
    isChecked,
    tornado,
    getReSurveyData,
    reSurveyDataVal,
    reSurveyDataFetched,
    resetReSurveyDataFetched,
    reportsData,
    reportsDataFetched,
    getReports,
}) {
    const {
        survey_version = '',
        quartileColors = [],
        year = '',
        lang = '',
        ohid: currOhid = '',
        report_id = '',
        report_type: surveyType = '',
    } = defaultSettings;
    const { accessToken } = AuthStore;
    const staticText = getStaticText(lang);
    const { SITE_TEXT } = staticText;
    const {
        VIEW_MORE,
        VIEW_LESS,
        BENCHMARK_HEAD,
        HEALTH_PRACTICE_RANKING,
        BOTTOM_RANKING,
        MIDDLE_RANKING,
        TOP_RANKING,
        RANK,
        PRACTICES,
        // RANKING,
        SCORE_TXT,
        NUMBER_OF_SURVEYS,
        YEAR,
        ORGANIZATION,
        COMPARISON_SURVEY,
        DIFF_HEAD,
    } = SITE_TEXT || {};
    const { $white } = colors;
    const { ohid = '', year: compYear = '' } = compOhid || {};
    const listRef = useRef(null);
    const [resurveyRanking, setResurveyRanking] = useState([]);
    const [showRanking, setShowRanking] = useState(1);
    const [isBothV4, setBothV4] = useState(false);
    const getFilterParams = filterItem => {
        const { demographics = [], benchmarks = [] } = filterItem[1] || {};
        return updateFilterParams(demographics, benchmarks);
    };

    useEffect(() => {
        if (!reportsDataFetched && accessToken) {
            getReports();
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isEmpty(reSurveyDataVal)) {
            resetReSurveyDataFetched(false);
        }
    }, [reSurveyDataVal]);

    const getData = newFilters => {
        const RESURVEY_PARAMS = getResurveyParams(newFilters);
        getReSurveyData(RESURVEY_PARAMS);
    };

    useEffect(() => {
        if (!reSurveyDataFetched && !apiLoadingCount) {
            const allDataReceived = filters.filter(({ benchmarks: cBen, demographics: cDen }) => cBen && cDen).length;
            if (allDataReceived === filters.length) {
                getData(filters);
            }
        }
        // eslint-disable-next-line
    }, [reSurveyDataFetched, filters, apiLoadingCount, apiParams]);
    useEffect(() => {
        const practiceDataCompYrFetched = ohid in practiceDataCompYr;
        if (!practiceDataCompYrFetched && !apiLoadingCount) {
            const { filters: filtersApplied = [], benchmarks = '' } = getFilterParams(filters);
            getComparisonYrPracticeData({
                ...apiParams,
                ...compOhid,
                filters: filtersApplied,
                benchmarks,
                report_type: apiParams?.report_type || surveyType,
            });
        }
        // eslint-disable-next-line
    }, [apiParams, practiceDataCompYr, compOhid, surveyType]);

    const practiceResurveyDataVal = practiceResurveyData?.rank || [];

    useEffect(() => {
        if (resurveyDataHere?.rank?.length > 0 && practiceResurveyDataVal.length > 0) {
            // Build score/quartile map from practiceData (ranking API - report year)
            const { ranking: practiceRanking = [] } = practiceData || {};
            const { items: practiceItems = [] } = practiceRanking[0] || {};

            const reportYearScoreMap = {}; // practice -> report year score
            const reportYearQuartileMap = {}; // practice -> report year quartile
            practiceItems.forEach(item => {
                if (item.text1 || item.text2) {
                    const practiceName = item.text1 || item.text2;
                    reportYearScoreMap[practiceName] = item.score;
                    reportYearQuartileMap[practiceName] = item.quartile;
                }
            });

            // Build score/quartile map from practiceDataCompYr (comparison year)
            const { ranking: compYearRanking = [] } = practiceDataCompYr[ohid] || {};
            const { items: compYearItems = [] } = compYearRanking[0] || {};

            const compYearScoreMap = {}; // practice -> comparison year score
            const compYearQuartileMap = {}; // practice -> comparison year quartile
            compYearItems.forEach(item => {
                if (item.text1 || item.text2) {
                    const practiceName = item.text1 || item.text2;
                    compYearScoreMap[practiceName] = item.score;
                    compYearQuartileMap[practiceName] = item.quartile;
                }
            });

            // Build maps to lookup ranks for each practice from resurvey ranking data
            // text1/index1 = report year (current), text2/index2 = comparison year
            const reportYearRankMap = {}; // practice -> report year rank (from text1/index1)
            const compYearRankMap = {}; // practice -> comparison year rank (from text2/index2)
            const valueMap = {}; // practice -> value
            const barColorMap = {}; // practice -> bar_color

            practiceResurveyDataVal.forEach(item => {
                // text1 with index1 = report year rank for that practice
                if (item.text1) {
                    reportYearRankMap[item.text1] = item.index1;
                    valueMap[item.text1] = item.value;
                    barColorMap[item.text1] = item.bar_color;
                }
                // text2 with index2 = comparison year rank for that practice
                if (item.text2) {
                    compYearRankMap[item.text2] = item.index2;
                }
            });

            // Get all unique practices from report year (text1)
            const allPractices = [...new Set(practiceResurveyDataVal.map(item => item.text1).filter(Boolean))];

            const rowsWithData = allPractices.map(practice => {
                const compRank = compYearRankMap[practice] ?? '-';
                const reportRank = reportYearRankMap[practice] ?? '-';

                // Combine scores and quartiles into arrays [reportYear, compYear]
                const reportScore = reportYearScoreMap[practice] ?? '-';
                const compScore = compYearScoreMap[practice] ?? '-';
                const reportQuartile = reportYearQuartileMap[practice] ?? '999';
                const compQuartile = compYearQuartileMap[practice] ?? '999';

                return {
                    compIndex: compRank, // Comparison year rank (from index2)
                    buIndex: reportRank, // Report year rank (from index1)
                    text1: practice, // Practice name
                    value: valueMap[practice] ?? '-',
                    score: [reportScore, compScore],
                    quartile: [reportQuartile, compQuartile],
                    barColor: barColorMap[practice] || '#FFFFFF',
                };
            });

            // Sort by report year rank (buIndex) by default
            const sortedData = [...rowsWithData].sort((a, b) => {
                if (a.buIndex === '-') return 1;
                if (b.buIndex === '-') return -1;
                return a.buIndex - b.buIndex;
            });

            if (isToggled) {
                // Sort by comparison year rank when toggled
                const toggleSortedData = [...rowsWithData].sort((a, b) => {
                    if (a.compIndex === '-') return 1;
                    if (b.compIndex === '-') return -1;
                    return a.compIndex - b.compIndex;
                });
                setResurveyRanking(toggleSortedData);
            } else {
                setResurveyRanking(sortedData);
            }
        }
        // eslint-disable-next-line
    }, [resurveyDataHere, practiceResurveyData, isToggled, practiceData, practiceDataCompYr, ohid]);

    const [isSticky, setIsSticky] = useState(false);
    const scrollIndex = useRef(null);
    const { ranking: recipeArrCurr = [], nSize = '', n_respondents, n_survey } = practiceData || {};
    const {
        ranking: recipeArrComp = [],
        nSize: compareSize = '',
        n_respondents: compareRespondents = '',
        n_survey: compareSurvey,
    } = practiceDataCompYr[ohid] || {};
    const { items: mainRanks = [] } = recipeArrCurr[0] || {};
    const { items: ranks = [] } = recipeArrComp[0] || {};
    const isV4 = isResurveyRankingSurveyVersion(survey_version);
    const [scroll, setScroll] = useState(-1);
    const [activeBtn, setActiveBtn] = useState(-1);
    const [showAll, setShowAll] = useState(false);
    const [showCompAll, setShowCompAll] = useState(false);
    const practiceBuName = 'Report year rank';
    const practiceReportName = 'Comparison year rank';
    const middleNumber = isChecked ? 10 : 11;

    const LightTooltip = withStyles(theme => ({
        arrow: {
            color: theme.palette.common.black,
        },
        tooltip: {
            backgroundColor: '#333333',
            fontSize: 14,
            fontStyle: 'italic',
            fontWeight: 400,
        },
    }))(Tooltip);

    const toggleShowAll = () => {
        setShowAll(!showAll);
    };

    const toggleCompShowAll = () => {
        setShowCompAll(!showCompAll);
    };

    useEffect(() => {
        const resurveyParams = getResurveyParams(filters);

        if (currOhid && resurveyParams?.ohid && reportsData[report_id]) {
            const allSurveys = reportsData[report_id];

            // Get last OHID from comma-separated string
            const resurveyOhids = resurveyParams.ohid.split(',');
            const selectedOhid = resurveyOhids[resurveyOhids.length - 1]?.trim();

            // Find the matching survey object
            const selectedSurvey = allSurveys.find(survey => survey.survey_id === selectedOhid);

            const selectedSurveyVersion = selectedSurvey?.survey_version;

            const isSelectedV4 = isResurveyRankingSurveyVersion(selectedSurveyVersion);

            setBothV4(isV4 && isSelectedV4);
        } else {
            setBothV4(false);
        }
    }, [reportsData, currOhid, filters, isV4]);

    const lastPracticeResurveyRankKeyRef = useRef(null);

    useEffect(() => {
        if (!isBothV4) {
            lastPracticeResurveyRankKeyRef.current = null;
            return;
        }
        if (apiLoadingCount) return;

        const resurveyParams = getResurveyParams(filters);
        const { threshold } = apiParams || {};
        const payload = {
            pid: '',
            ohid: resurveyParams.ohid,
            filters: resurveyParams.filters || [],
            lang: resurveyParams.lang,
            threshold,
            report_type: apiParams?.report_type || surveyType,
        };
        const key = JSON.stringify(payload);

        if (lastPracticeResurveyRankKeyRef.current === key) return;
        lastPracticeResurveyRankKeyRef.current = key;
        getPracticeResurveyData(payload);
    }, [
        apiParams,
        apiLoadingCount,
        isBothV4,
        filters,
        getResurveyParams,
        getPracticeResurveyData,
        surveyType,
        compOhid?.ohid,
        compOhid?.year,
    ]);

    const handleAllFilterData = (
        parKey,
        childKey,
        demographic,
        filtersHere,
        updateRedux = true,
        activeFilter = true
    ) => {
        const newDemographic = cloneDeep(demographic);
        const newParDemoIndex = newDemographic.findIndex(({ code }) => code === parKey);
        const selectedItemIndex = newDemographic[newParDemoIndex].options.findIndex(({ code }) => code === childKey);
        newDemographic[newParDemoIndex].options[selectedItemIndex].isSelected = false;
        const { benchmarks = [] } = filtersHere[0];
        const allList = { demographics: newDemographic, benchmarks };
        const newList = [...filters.slice(0, 0), allList, ...filters.slice(0 + 1)];
        if (updateRedux === true) {
            setFilters(newList, true, activeFilter, 0);
        }
    };

    const handleAllCompFilter = (
        parKey,
        childKey,
        compDemographic,
        buFilterHere,
        updateRedux = true,
        activeFilter = true
    ) => {
        const newDemographic = cloneDeep(compDemographic);
        const newParDemoIndex = newDemographic.findIndex(({ code }) => code === parKey);
        const selectedItemIndex = newDemographic[newParDemoIndex].options.findIndex(({ code }) => code === childKey);
        newDemographic[newParDemoIndex].options[selectedItemIndex].isSelected = false;
        const { benchmarks: compBenchmark = [] } = buFilterHere[1];
        const allList = { demographics: newDemographic, benchmarks: compBenchmark };
        const newList = [...filters.slice(0, 1), allList];
        if (updateRedux === true) {
            setFilters(newList, true, activeFilter, 1);
        }
    };

    const maxItemsToShow = 6;

    useEffect(() => {
        // eslint-disable-next-line no-unused-expressions
        scrollIndex.current?.scrollIntoView({ behavior: 'instant' });
    }, [scroll]);

    const switchBtnHighlight = () => {
        const mid = document.getElementById('mid');
        const bottom = document.getElementById('bottom');
        const midOffSet = mid.offsetTop + 200;
        const bottomOffSet = bottom.offsetTop + 200;
        if (window.scrollY > midOffSet && window.scrollY < bottomOffSet) {
            setActiveBtn(11);
        } else if (window.scrollY > bottomOffSet) {
            setActiveBtn(mainRanks.length - 13);
        } else {
            setActiveBtn(-1);
        }
    };

    const listenToScroll = () => {
        const handleScroll = () => {
            const offset = window.scrollY;
            setIsSticky(offset > 200);
            const itemHeight = 200;
            const scrollPosition = window.scrollY || document.documentElement.scrollTop;
            const targetIndex = Math.floor(scrollPosition / itemHeight);

            if (targetIndex >= 0 && targetIndex <= 2) {
                setShowRanking(1);
            } else if (targetIndex >= 3 && targetIndex <= 6) {
                setShowRanking(2);
            } else if (targetIndex >= 6 && targetIndex <= 7) {
                setShowRanking(3);
            }
        };
        handleScroll();
        switchBtnHighlight();
    };

    useEffect(() => {
        window.addEventListener('scroll', listenToScroll);
        return () => {
            window.removeEventListener('scroll', listenToScroll);
        };
        // eslint-disable-next-line
    }, []);
    const renderComparisonRows = ({ rank }) => {
        const currItemLength = mainRanks.length;
        const compItemLength = ranks.length;
        const lastMidRankComp = compItemLength - 12;
        const currBottomRank = currItemLength - 12;
        const delta = currItemLength - compItemLength;
        const skipPosition = delta > 0 && rank > lastMidRankComp && rank <= currBottomRank;
        const rankToUse = rank > lastMidRankComp ? rank - delta : rank;

        const { text2: practice = '' } = ranks[rankToUse - 1] || {};
        if (!ranks.length) return null;
        return (
            <Fragment>
                <div
                    className="practiceIndex"
                    style={{
                        width: '5%',
                        textAlign: 'center',
                    }}
                >
                    <div className="orgRanks">
                        <span
                            className={c('compRound comp', {
                                topRank: rankToUse < 13,
                            })}
                        >
                            {skipPosition ? '' : rankToUse}
                        </span>
                    </div>
                </div>
                <div style={{ width: '45%' }} className={c('practiceText', {})}>
                    {skipPosition ? '' : practice}
                </div>
            </Fragment>
        );
    };

    const renderRows = ({ rank, practice }) => {
        if (!ranks.length) return null;
        return (
            <Fragment>
                <div
                    className="practiceIndex"
                    style={{
                        width: '5%',
                        textAlign: 'center',
                    }}
                >
                    <div className="orgRanks">
                        <span
                            className={c('compRound comp', {
                                topRank: rank < 13,
                            })}
                        >
                            {!practice ? '' : rank}
                        </span>
                    </div>
                </div>
                <div style={{ width: '45%' }} className={c('practiceText', {})}>
                    {practice}
                </div>
            </Fragment>
        );
    };

    const handleClicks = (position, index) => {
        setScroll(index);
        setActiveBtn(index);
        if (listRef.current && !tornado) {
            const listItems = listRef.current.querySelectorAll('.practiceContent');

            if (position === 'top') {
                window.scrollTo({ top: 0, behavior: 'instant' });
            } else if (position === 'medium') {
                listItems[index].scrollIntoView({ behavior: 'instant', block: 'start' });
            } else if (position === 'bottom') {
                const listItem = listRef.current.children[practiceResurveyDataVal.length - 12];
                listItem.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'nearest' });
            }

            if (index === 0) {
                setShowRanking(1);
            } else if (index === 11) {
                setShowRanking(2);
            } else if (index > 11) {
                setShowRanking(3);
            }
        }
    };

    const resurveyDemoName1 = AuthStore.resurveyFilter1 || ORGANIZATION;
    const resurveyDemoName2 = AuthStore.resurveyFilter2 || COMPARISON_SURVEY;

    const appliedBuFiltersContent = () => {
        const { demographics: budemographics = [], benchmarks: compBenchmarks = [] } = filters[1] || {};
        const compDemographic = budemographics || [];
        const { benchmarks: bm = [], demographics = [] } = filters[0] || {};
        const { filters: filterApplied = [] } = apiParams;
        const label = getBenchmarkName(bm);
        const { filters: compFilter = [] } = updateFilterParams(compDemographic, compBenchmarks) || {};

        const arrItem = getFiltersAppliedText(filterApplied, demographics);
        const newItem = getOutputFilter(filterApplied, demographics);
        const arrItemComp = getFiltersAppliedText(compFilter, compDemographic);
        const newItemComp = getOutputFilter(compFilter, compDemographic);

        return (
            <div className="filterAppliedContent">
                {arrItem.length > 0 && newItem !== null && (
                    <div className="filterDemo">
                        {newItem !== null && (
                            <div className="heading">
                                {resurveyDemoName1} {`(${year} - ${currOhid})`}:
                            </div>
                        )}
                        <div className="nsizeContent">
                            <div>n : </div>
                            <div>{nSize}</div>
                        </div>
                        {newItem !== null &&
                            newItem
                                .slice(0, showAll ? newItem.length : maxItemsToShow)
                                .map(({ parentK, childKey, parentCode, childCode }) => {
                                    return (
                                        <div className="filterContent">
                                            <LightTooltip title={`${parentK} - ${childKey}`} arrow>
                                                <div
                                                    key={parentCode}
                                                    className="filterHighlight filterKey"
                                                >{`${childKey}`}</div>
                                            </LightTooltip>
                                            <img
                                                src={FocusArea}
                                                alt=""
                                                className="filterImg"
                                                onClick={() => {
                                                    handleAllFilterData(parentCode, childCode, demographics, filters);
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                        {(newItem.length >= maxItemsToShow || showAll) && (
                            <div onClick={toggleShowAll}>
                                {showAll ? (
                                    <div className="viewIconContent">
                                        <img src={viewIcon} alt="" className="viewImg" />
                                        <div className="viewText">{VIEW_LESS}</div>
                                    </div>
                                ) : (
                                    <div className="viewIconContent">
                                        <img src={viewIcon} alt="" className="viewImg" />
                                        <div className="viewText">{VIEW_MORE}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {arrItemComp.length > 0 && newItemComp !== null && (
                    <div className="filterDemo">
                        {newItemComp !== null && (
                            <div className="heading">
                                {resurveyDemoName2} {`(${compYear} - ${ohid})`}:
                            </div>
                        )}
                        <div className="nsizeContent">
                            <div>n : </div>
                            <div>{compareSize}</div>
                        </div>
                        {newItemComp !== null &&
                            newItemComp
                                .slice(0, showCompAll ? newItemComp.length : maxItemsToShow)
                                .map(({ parentK, childKey, parentCode, childCode }) => {
                                    return (
                                        <div className="filterContent">
                                            <LightTooltip title={`${parentK} - ${childKey}`} arrow>
                                                <div
                                                    key={parentCode}
                                                    className="filterHighlight filterKey"
                                                >{`${childKey}`}</div>
                                            </LightTooltip>
                                            <img
                                                src={FocusArea}
                                                alt=""
                                                className="filterImg"
                                                onClick={() => {
                                                    handleAllCompFilter(
                                                        parentCode,
                                                        childCode,
                                                        compDemographic,
                                                        filters
                                                    );
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                        {(newItemComp.length >= maxItemsToShow || showCompAll) && (
                            <div onClick={toggleCompShowAll}>
                                {showCompAll ? (
                                    <div className="viewIconContent">
                                        <img src={viewIcon} alt="" className="viewImg" />
                                        <div className="viewText">{VIEW_LESS}</div>
                                    </div>
                                ) : (
                                    <div className="viewIconContent">
                                        <img src={viewIcon} alt="" className="viewImg" />
                                        <div className="viewText">{VIEW_MORE}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <div className="filterDemo">
                    <div className="heading">{BENCHMARK_HEAD + `:`}</div>
                    <div className="nsizeContent">
                        <div>{YEAR} : </div>
                        <div>{year}</div>
                    </div>
                    <div className="nsizeContent">
                        <div>n :</div>
                        <div>{n_respondents}</div>
                    </div>
                    <div className="nsizeContent">
                        <div>{NUMBER_OF_SURVEYS} :</div>
                        <div>{n_survey}</div>
                    </div>
                    <div className="filterContent">
                        <div className="filterHighlight filterKey filterLab">{label}</div>
                    </div>

                    <div className="heading">{BENCHMARK_HEAD + `:`}</div>
                    <div className="nsizeContent">
                        <div>{YEAR} : </div>
                        <div>{compYear}</div>
                    </div>
                    <div className="nsizeContent">
                        <div>n :</div>
                        <div>{compareRespondents}</div>
                    </div>
                    <div className="nsizeContent">
                        <div>{NUMBER_OF_SURVEYS} :</div>
                        <div>{compareSurvey}</div>
                    </div>
                    <div className="filterContent">
                        <div className="filterHighlight filterKey filterLab">{getBenchmarkName(compBenchmarks)}</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Fragment>
            <Helmet>
                <title>{HEALTH_PRACTICE_RANKING}</title>
            </Helmet>
            <div className="newContentForBu">{appliedBuFiltersContent()}</div>
            {mainRanks.length && ranks.length && isV4 && !isBothV4 ? (
                <div className="graphContent buGraphContent">
                    <div className="groupSection">
                        <div className={c('sideScrolling resurvey', { stickyBtn: isSticky })}>
                            <div
                                className={c('rankingScroll', { active: activeBtn === -1 })}
                                onClick={() => handleClicks(-1)}
                            >
                                <div className="ranking">{TOP_RANKING}</div>
                            </div>
                            <div
                                className={c('rankingScroll', { active: activeBtn === 11 })}
                                onClick={() => handleClicks(11)}
                            >
                                <div className="ranking">{MIDDLE_RANKING}</div>
                            </div>
                            <div
                                className={c('rankingScroll', {
                                    active: activeBtn === Object.keys(mainRanks).length - 13,
                                })}
                                onClick={() => handleClicks(mainRanks.length - 13)}
                            >
                                <div className="ranking">{BOTTOM_RANKING}</div>
                            </div>
                        </div>

                        <div className="rankingBuSection resurveyBoard" ref={scroll === -1 ? scrollIndex : null}>
                            <div className="headerNav top">
                                <div className="headingContent rankHead">
                                    {resurveyDemoName1} {`(${year} - ${currOhid})`}
                                </div>
                                <div className="headingContent practice">{}</div>
                                <div className="headingContent rankHead">
                                    {resurveyDemoName2} {`(${compYear} - ${ohid})`}
                                </div>
                                <div className="headingContent practice">{}</div>
                            </div>
                            <div className="headerNav bottom">
                                <div className="headingContent rankHead">{RANK}</div>
                                <div className="headingContent practice">{PRACTICES}</div>
                                <div className="headingContent rankHead">{RANK}</div>
                                <div className="headingContent practice">{PRACTICES}</div>
                            </div>
                            <div className="mainContent resurveyBoard">
                                {mainRanks.map(({ text2: practiceName = '', index: rank }) => {
                                    const topRanking = rank === 13;
                                    const bottomRanking = rank === mainRanks.length - 11;
                                    const styleArr = {};
                                    const id = rank === 12 ? 'mid' : '';
                                    const idToUse = rank === mainRanks.length - 12 ? 'bottom' : id;
                                    if (rank < 13) {
                                        styleArr.color = '#0679C3';
                                        styleArr.fontWeight = 500;
                                    }
                                    if (rank > mainRanks.length - 12) {
                                        styleArr.color = '#FF0000';
                                    }
                                    if (topRanking || bottomRanking) {
                                        styleArr.borderTop = '1px solid #CCCCCC';
                                    }
                                    return (
                                        <div
                                            className="practiceContent"
                                            key={rank}
                                            style={styleArr}
                                            ref={scroll === rank - 1 ? scrollIndex : null}
                                            id={idToUse}
                                        >
                                            {renderRows({
                                                rank,
                                                practice: practiceName,
                                            })}
                                            {renderComparisonRows({
                                                rank,
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* {reSurveyFilter && (
                <FilterBar
                    activeResetFilter={activeFilter}
                    size={[nSize, 0]}
                    surveyRespondents={[n_respondents, 0]}
                    surveys={[n_survey, 0]}
                    filters={[filters[0], { demographics: practiceFilters }]}
                    projectInFilter={BU_PROJECTS}
                    currentView={6}
                    showBenchmarks={false}
                    closeFilters={updateFilterView}
                    setFilters={setFilters}
                    defaultBenchmark={benchmark}
                    defaultSettings={defaultSettings}
                />
            )} */}
            <div className="graphContent buGraphContent">
                {resurveyRanking.length > 0 && practiceResurveyDataVal.length > 0 && isBothV4 && (
                    <div className="groupSection">
                        <div
                            className={c('sideScrolling', {
                                stickyBtn: isSticky,
                                scoreScrolling: isChecked,
                                scoreStickyTab: isSticky && isChecked,
                            })}
                        >
                            <div
                                className={c('rankingScroll', { active: showRanking === 1 && !tornado })}
                                onClick={() => handleClicks('top', 0)}
                            >
                                <div className="ranking">{TOP_RANKING}</div>
                            </div>
                            <div
                                className={c('rankingScroll', { active: showRanking === 2 && !tornado })}
                                onClick={() => handleClicks('medium', middleNumber)}
                            >
                                <div className="ranking">{MIDDLE_RANKING}</div>
                            </div>
                            <div
                                className={c('rankingScroll', { active: showRanking === 3 && !tornado })}
                                onClick={() => handleClicks('bottom', practiceResurveyDataVal.length - 12)}
                            >
                                <div className="ranking">{BOTTOM_RANKING}</div>
                            </div>
                        </div>

                        <div className="rankingBuSection">
                            <div className="headerNav">
                                <div
                                    style={{ width: '35%', padding: '1.5vh' }}
                                    className="headingContent headingBuComparison"
                                >
                                    {PRACTICES}
                                </div>
                                <div style={{ width: '15%', textAlign: 'center' }} className="headingContent">
                                    <div style={{ padding: '1.5vh' }}>{practiceBuName}</div>
                                    {isChecked && (
                                        <div className="showScores">
                                            <div className="checkHeader">{RANK}</div>
                                            <div className="checkHeader headerBorder">{SCORE_TXT}</div>
                                        </div>
                                    )}
                                </div>
                                <div style={{ width: '15%', textAlign: 'center' }} className="headingContent">
                                    <div style={{ padding: '1.5vh' }}>{practiceReportName}</div>
                                    {isChecked && (
                                        <div className="showScores">
                                            <div className="checkHeader">{RANK}</div>
                                            <div className="checkHeader headerBorder">{SCORE_TXT}</div>
                                        </div>
                                    )}
                                </div>

                                <div
                                    style={{ width: '35%', textAlign: 'center', padding: '1.5vh' }}
                                    className="headingContent"
                                >
                                    {DIFF_HEAD}
                                </div>
                            </div>
                            <div className="mainContent" ref={listRef}>
                                {resurveyRanking.map(item => {
                                    const { color, background = $white, border = colors.$grey200 } =
                                        quartileColors[item.quartile[0]] || {};
                                    const {
                                        color: buColor,
                                        background: buBackground = $white,
                                        border: buBorder = colors.$grey200,
                                    } = quartileColors[item.quartile[1]] || {};
                                    const styleBlack =
                                        item.quartile[0] === ('' || 999) ? { border: '2px solid #000000' } : {};
                                    const listsWithBottomBorder = 12;
                                    const newItem = practiceResurveyDataVal.length - listsWithBottomBorder;
                                    const isCompIndexNumeric = typeof item.compIndex === 'number';
                                    const isBuIndexNumeric = typeof item.buIndex === 'number';
                                    return (
                                        <div
                                            className={c('practiceContent', {
                                                newbottomBorder:
                                                    isBuIndexNumeric && item.buIndex <= 12 && !isChecked && !tornado,
                                                newbottom: item.buIndex === newItem && !isChecked && !tornado,
                                            })}
                                        >
                                            <div
                                                style={{ width: '35%' }}
                                                className={c('practiceText', {
                                                    topRank: isBuIndexNumeric && item.buIndex <= 12,
                                                    topLastRank: isBuIndexNumeric && item.buIndex >= newItem + 1,
                                                })}
                                            >
                                                {item.text1}
                                            </div>

                                            {/* Report year rank (buIndex) - first column */}
                                            <div
                                                style={{ width: '15%', textAlign: 'center' }}
                                                className="practiceIndex"
                                            >
                                                <div className="orgRanks">
                                                    <span
                                                        className={c('compRound', {
                                                            comp: isBuIndexNumeric && item.buIndex <= 12,
                                                            topRank: isBuIndexNumeric && item.buIndex <= 12,
                                                            topLastRank:
                                                                isBuIndexNumeric && item.buIndex >= newItem + 1,
                                                        })}
                                                    >
                                                        {item.buIndex}
                                                    </span>
                                                </div>
                                                {isChecked && (
                                                    <div className="orgRanks orgBorder">
                                                        <span
                                                            style={{
                                                                background,
                                                                color,
                                                                ...styleBlack,
                                                            }}
                                                            className="scoreContent"
                                                        >
                                                            <div
                                                                style={{
                                                                    borderColor: `${border} transparent transparent transparent`,
                                                                }}
                                                                className="scoreElement"
                                                            />
                                                            <span>{item.score[0]}</span>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Comparison year rank (compIndex) - second column */}
                                            <div
                                                style={{ width: '15%', textAlign: 'center' }}
                                                className="practiceIndex"
                                            >
                                                <div className="orgRanks">
                                                    <span
                                                        className={c('compRound', {
                                                            comp: isCompIndexNumeric && item.compIndex <= 12,
                                                            topRank: isCompIndexNumeric && item.compIndex <= 12,
                                                            topLastRank:
                                                                isCompIndexNumeric && item.compIndex >= newItem + 1,
                                                        })}
                                                    >
                                                        {item.compIndex}
                                                    </span>
                                                </div>
                                                {isChecked && (
                                                    <div className="orgRanks orgBorder">
                                                        <span
                                                            style={{
                                                                background: buBackground,
                                                                color: buColor,
                                                                ...styleBlack,
                                                            }}
                                                            className="scoreContent"
                                                        >
                                                            <div
                                                                style={{
                                                                    borderColor: `${buBorder} transparent transparent transparent`,
                                                                }}
                                                                className="scoreElement"
                                                            />
                                                            <span>{item.score[1]}</span>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div
                                                className="clearfix newRank"
                                                style={{
                                                    width: '35%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <div
                                                    className={c('rank rankContent', {
                                                        negative: typeof item.value === 'number' && item.value < 0,
                                                    })}
                                                    style={{ top: 0 }}
                                                >
                                                    <div
                                                        className={c('textBorder')}
                                                        style={{
                                                            width:
                                                                typeof item.value === 'number'
                                                                    ? `${2 * Math.abs(item.value)}%`
                                                                    : '0%',
                                                            background: `${item.barColor}`,
                                                        }}
                                                    >
                                                        <div className="textRank">{item.value}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="graphHighlight">
                                    <div className="highLight" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {isBothV4 && (
                    <div className={c('legendSpace', { activeScroll: isChecked })}>
                        <div>
                            <ul className="legends clearfix" style={{ marginTop: '0' }}>
                                <li style={{ fontWeight: '500', margin: '0 1vw 0 0', textTransform: 'none' }}>
                                    Difference between ranks :
                                </li>
                                <li>
                                    <span className="bullet graphTextNewME" />
                                    More emphasized
                                </li>
                                <li>
                                    <span className="bullet graphTextNewLE" />
                                    Less emphasized
                                </li>
                                <li>
                                    <span className="bullet graphTextNewC" />
                                    Comparable
                                </li>
                            </ul>
                        </div>
                        <div>
                            <p className="note">NOTE:</p>
                            <ol>
                                <li className="list">
                                    Differences between ranks are calculated by subtracting the rank for the current
                                    year from the rank for the previous year; order of differences shown corresponds to
                                    current year practice rank.
                                </li>
                                <li className="list">
                                    Practice(s) more emphasized in current year have at least +12 rank difference
                                    between current and previous years
                                </li>
                                <li className="list">
                                    Practice(s) less emphasized in current year have at least -12 rank difference
                                    between current and previous years
                                </li>
                            </ol>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
}

ResurveyBoard.propTypes = {
    apiParams: PropTypes.object.isRequired,
    apiLoadingCount: PropTypes.number.isRequired,
    practiceData: PropTypes.object.isRequired,
    defaultSettings: PropTypes.object.isRequired,
    practiceDataCompYr: PropTypes.object.isRequired,
    getComparisonYrPracticeData: PropTypes.func.isRequired,
    getPracticeResurveyData: PropTypes.func.isRequired,
    compOhid: PropTypes.object.isRequired,
    filters: PropTypes.array.isRequired,
    resurveyDataHere: PropTypes.array.isRequired,
    practiceResurveyData: PropTypes.array.isRequired,
    setFilters: PropTypes.func.isRequired,
    getResurveyParams: PropTypes.func.isRequired,
    reSurveyDataFetched: PropTypes.bool.isRequired,
    isToggled: PropTypes.bool.isRequired,
    isChecked: PropTypes.bool.isRequired,
    tornado: PropTypes.bool.isRequired,
    getReSurveyData: PropTypes.func.isRequired,
    reSurveyDataVal: PropTypes.object.isRequired,
    resetReSurveyDataFetched: PropTypes.func.isRequired,
    reportsData: PropTypes.array.isRequired,
    getReports: PropTypes.func.isRequired,
    reportsDataFetched: PropTypes.bool.isRequired,
};

export default ResurveyBoard;
